/**
 * ModalLayer — global modal/dialog/drawer portal layer.
 *
 * Locked 2026-05-30 — applies globally per owner directive:
 *   "Any popup, drawer, wizard, onboarding step, payment screen, customer
 *    screen, settings screen, etc. must always render above maps and
 *    background content."
 *
 * Architecture:
 *   - <ModalLayer /> is mounted ONCE inside each Provider surface
 *     (ProviderMobileApp and ProviderDesktopApp).
 *   - It creates a portal target node (`#remod-modal-layer`) at the
 *     document.body root with z-index 9000 — above maps (z 1-100),
 *     above workspace (z 1-1000), above sidebars.
 *   - Modal/dialog/drawer components use `useModalLayer()` to render
 *     INTO that portal, guaranteeing they always sit above background.
 *
 * Background z-index policy (enforced by viewModeOverrides.css and
 * component conventions):
 *   maps:                z 1-100
 *   workspace content:   z 100-1000
 *   sidebars / nav:      z 1000-5000
 *   ModalLayer (modals): z 9000+
 *   App-level overlays:  z 9500+ (impersonation banner, app-lock)
 */
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const LAYER_ID = 'remod-modal-layer';

const ensureLayerNode = () => {
  if (typeof document === 'undefined') return null;
  let node = document.getElementById(LAYER_ID);
  if (!node) {
    node = document.createElement('div');
    node.id = LAYER_ID;
    node.style.position = 'fixed';
    node.style.top = '0';
    node.style.left = '0';
    node.style.right = '0';
    node.style.bottom = '0';
    node.style.zIndex = '9000';
    node.style.pointerEvents = 'none'; // children opt into pointer events
    document.body.appendChild(node);
  }
  return node;
};

/**
 * ModalLayer — mount this once inside each surface. It guarantees the
 * portal target exists and tears it down when the surface unmounts.
 */
export const ModalLayer = () => {
  useEffect(() => {
    const node = ensureLayerNode();
    return () => {
      // Only tear down if empty — otherwise another surface re-mount may
      // race and we'd lose mounted modals.
      if (node && node.childNodes.length === 0) {
        node.remove();
      }
    };
  }, []);
  return null;
};

/**
 * useModalPortal — utility for modal components to render INTO the global
 * layer. Returns a `Portal` component that wraps children in createPortal.
 *
 * Usage:
 *   const Portal = useModalPortal();
 *   return <Portal><MyDialog /></Portal>;
 */
export const useModalPortal = () => {
  const nodeRef = useRef(null);
  if (!nodeRef.current) nodeRef.current = ensureLayerNode();
  return ({ children }) =>
    nodeRef.current ? createPortal(<div style={{ pointerEvents: 'auto' }}>{children}</div>, nodeRef.current) : null;
};

/**
 * ModalPortal — convenience wrapper component. Renders children into the
 * global modal layer.
 */
export const ModalPortal = ({ children }) => {
  const node = ensureLayerNode();
  return node ? createPortal(<div style={{ pointerEvents: 'auto' }}>{children}</div>, node) : null;
};

export default ModalLayer;
